import { getErrorName } from './utils';

/**
 * Key of an error's self-description method: `error[DESCRIBE](): ErrorDescription`.
 * Define it on the class (prototype), so it never shows in the error's own fields.
 */
export const DESCRIBE: unique symbol = Symbol('scdk.describe');

/** How an error presents itself in Sentry; every part optional. */
export interface ErrorDescription {
  /** Appended to the Issue title (e.g. `HTTP 500 GET /user/sip`); never part of the error itself. */
  detail?: string;
  /** Fingerprint parts identifying the failure, after the error name; without the feature that hit it. */
  grouping?: string[];
  /** Extra searchable tags (e.g. `endpoint`). */
  tags?: Record<string, string>;
}

/** `describeError` result: `grouping` always present. */
export interface DescribedError extends ErrorDescription {
  grouping: string[];
}

const readField = (value: unknown, key: PropertyKey): unknown => {
  if (typeof value !== 'object' || value === null) return undefined;
  try {
    return Reflect.get(value, key);
  } catch (e) {
    return undefined;
  }
};

/** `error[DESCRIBE]()`, or `{}` when absent or throwing. */
function ownDescription(error: unknown): ErrorDescription {
  const describe = readField(error, DESCRIBE);
  if (typeof describe !== 'function') return {};
  try {
    const own: ErrorDescription | undefined = describe.call(error);
    return own ?? {};
  } catch (e) {
    return {};
  }
}

/** Cause's own detail, else `<name>: <message>`. */
function causeSummary(cause: unknown, description: ErrorDescription): string {
  const name = getErrorName(cause);
  const message = readField(cause, 'message');
  return (
    description.detail ||
    (typeof message === 'string' && message ? `${name}: ${message}` : name)
  );
}

/**
 * Title detail, fingerprint parts and tags of any error, from its own `[DESCRIBE]()` and its
 * direct `cause` only (deeper links show as Sentry linked exceptions):
 * - `detail` = own, else `Cause: <cause detail, or cause name: message>`
 * - `grouping` = `[error name, ...own grouping, cause name?, ...cause grouping]`
 * - `tags` = cause tags overlaid by own tags
 */
export function describeError(error: unknown): DescribedError {
  const own = ownDescription(error);
  const cause = readField(error, 'cause');
  const hasCause = cause !== undefined && cause !== null;
  const fromCause = hasCause ? ownDescription(cause) : {};
  const tags = { ...fromCause.tags, ...own.tags };
  return {
    detail:
      own.detail ||
      (hasCause ? `Cause: ${causeSummary(cause, fromCause)}` : undefined),
    grouping: [
      getErrorName(error),
      ...(own.grouping ?? []),
      ...(hasCause ? [getErrorName(cause), ...(fromCause.grouping ?? [])] : []),
    ],
    tags: Object.keys(tags).length > 0 ? tags : undefined,
  };
}
